/*
 * @Author: linguang linguang@nextmar.com
 * @Date: 2025-04-12 09:38:47
 * @LastEditors: Gao Deng gaodeng@nextmar.com
 * @LastEditTime: 2025-06-09 22:07:01
 * @FilePath: /zeelool-nextjs/src/components/Dialog/index.tsx
 * @Description: 这是默认设置,请设置`customMade`, 打开koroFileHeader查看配置 进行设置: https://github.com/OBKoro1/koro1FileHeader/wiki/%E9%85%8D%E7%BD%AE
 */
'use client';

import {
  DialogActions,
  DialogContent,
  DialogContentProps,
  DialogTitle,
  Divider,
  IconButton,
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CloseOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { ReactNode, forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import Button from '../Button';
import logger from '@/utils/client-logger';

export interface DialogProps extends Omit<MuiDialogProps, 'title'> {
  /**
   * 弹窗标题
   */
  title?: ReactNode;
  /**
   * 是否显示关闭按钮
   * @default true
   */
  showCloseIcon?: boolean;
  /**
   * 弹窗内容
   */
  children?: ReactNode;
  /**
   * 底部操作区内容
   */
  footer?: ReactNode;
  /**
   * 确认按钮文本
   * @default 'Ok'
   */
  okText?: string;
  /**
   * 取消按钮文本
   * @default 'Cancel'
   */
  cancelText?: string;
  /**
   * 点击遮罩层是否关闭弹出层
   * @default true
   */
  closeOnClickOverlay?: boolean;
  /**
   * 确认按钮点击回调
   */
  onOk?: () => void;
  /**
   * 取消按钮点击回调
   */
  onCancel?: () => void;
  /**
   * 是否显示标题下方的分割线
   * @default false
   */
  showTitleDivider?: boolean;
  /**
   * 内容区域的props
   */
  contentProps?: DialogContentProps;
  /**
   * 是否显示加载状态
   * @default false
   */
  loading?: boolean;
  /**
   * 是否隐藏标题（Header）
   * 如果为 true，则不会渲染 DialogTitle、关闭按钮以及标题下方的分割线
   * @default false
   */
  hideHeader?: boolean;
  /**
   * hideHeader 为 true 时用于 aria-label（无可见标题时无障碍名称）
   */
  ariaLabel?: string;
}

/**
 * Dialog.Confirm 确认弹窗的属性
 */
interface ConfirmDialogProps extends Omit<DialogProps, 'footer'> {
  /**
   * 确认按钮文本
   * @default 'Ok'
   */
  okText?: string;
  /**
   * 确认按钮点击回调
   */
  onOk?: () => void;
  /**
   * 确认按钮是否显示加载状态
   * @default false
   */
  loading?: boolean;
}

/**
 * Dialog.Info 信息弹窗的属性
 */
interface InfoDialogProps extends Omit<DialogProps, 'footer' | 'showCloseIcon'> {}

// 扩展 DialogType 接口，添加 Confirm 和 Info 静态属性
interface DialogType
  extends React.ForwardRefExoticComponent<DialogProps & React.RefAttributes<HTMLDivElement>> {
  Confirm: React.ForwardRefExoticComponent<
    ConfirmDialogProps & React.RefAttributes<HTMLDivElement>
  >;
  Info: React.ForwardRefExoticComponent<InfoDialogProps & React.RefAttributes<HTMLDivElement>>;
}

/**
 * Dialog 弹窗组件
 * 基于 MUI Dialog 组件开发，根据视觉规范进行样式调整
 */
const DialogBase = forwardRef<HTMLDivElement, DialogProps>((props, ref) => {
  const t = useTranslations('dialog');
  const {
    open,
    title,
    children,
    footer,
    showCloseIcon = true,
    hideHeader = false,
    ariaLabel,
    onClose,
    contentProps,
    okText = t('okText'),
    cancelText = t('cancelText'),
    onOk,
    onCancel,
    showTitleDivider = false,
    loading = false,
    closeOnClickOverlay = true,
    disableRestoreFocus = true,
    ...otherProps
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const wasOpenRef = useRef(false);
  const hideHeaderFocusScheduledRef = useRef(false);
  const hideHeaderContentRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // 清理所有的定时器和 requestAnimationFrame
  const cleanupFocusTimers = () => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  };

  useLayoutEffect(() => {
    if (open) {
      if (!wasOpenRef.current) {
        previousFocusRef.current = document.activeElement as HTMLElement | null;
        wasOpenRef.current = true;
        hideHeaderFocusScheduledRef.current = false;
      }

      if (!hideHeader || !hideHeaderContentRef.current) {
        cleanupFocusTimers();
        return;
      }

      if (hideHeaderFocusScheduledRef.current) {
        return;
      }
      hideHeaderFocusScheduledRef.current = true;

      const focusable = hideHeaderContentRef.current.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      );

      const setFocus = () => {
        const target = focusable ?? hideHeaderContentRef.current;
        try {
          target?.focus?.();
        } catch (err) {
          logger.error('[Dialog] Failed to focus:', err, target);
        }
      };

      let focusCommitted = false;
      const tryFocus = () => {
        if (focusCommitted) return;
        focusCommitted = true;
        cleanupFocusTimers();
        setFocus();
      };

      rafIdRef.current = requestAnimationFrame(tryFocus);
      timeoutIdRef.current = setTimeout(tryFocus, 100);

      return cleanupFocusTimers;
    }

    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      hideHeaderFocusScheduledRef.current = false;
      cleanupFocusTimers();

      const restoreFocus = () => {
        if (previousFocusRef.current) {
          try {
            previousFocusRef.current.focus();
          } catch (err) {
            logger.error('[Dialog] Failed to restore focus:', err);
          } finally {
            previousFocusRef.current = null;
          }
        }
      };

      let restoreCommitted = false;
      const tryRestore = () => {
        if (restoreCommitted) return;
        restoreCommitted = true;
        cleanupFocusTimers();
        restoreFocus();
      };

      rafIdRef.current = requestAnimationFrame(tryRestore);
      timeoutIdRef.current = setTimeout(tryRestore, 100);
      return cleanupFocusTimers;
    }

    cleanupFocusTimers();
  }, [open, hideHeader]);

  // 组件卸载时清理所有定时器
  useEffect(() => {
    return cleanupFocusTimers;
  }, []);

  useEffect(() => {
    if (open) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      const hasOpenMuiModal = document.querySelector('.MuiModal-root') !== null;

      // 仅在页面上没有其他 MUI Modal/Drawer 时，兜底恢复 body 滚动。
      // 避免嵌套弹窗卸载时误把父弹窗的 scroll lock 清掉。
      if (!hasOpenMuiModal && document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [open]);

  useEffect(() => {
    return () => {
      // 与 `[open]` 兜底逻辑一致：子弹窗卸载时页面可能仍存在父 Modal（嵌套 Dialog）。
      // 例如 TryOn 外层 Dialog + ImageTryOn 内始终挂载的删除确认 Dialog，切换到 AR 时内层卸载
      // 若此处无条件重置 body overflow，会误清父级的 scroll lock，导致滚轮穿透遮罩滚动底层页面。
      window.requestAnimationFrame(() => {
        const hasOpenMuiModal = document.querySelector('.MuiModal-root') !== null;

        if (!hasOpenMuiModal && document.body.style.overflow === 'hidden') {
          document.body.style.overflow = '';
        }
      });
    };
  }, []);

  const handleClose = (
    e: React.MouseEvent<HTMLDivElement>,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (reason === 'backdropClick' && !closeOnClickOverlay) {
      return;
    }
    if (onClose) {
      onClose?.(e, reason);
    }
  };

  return (
    <MuiDialog
      ref={ref}
      open={open}
      onClose={handleClose}
      aria-labelledby={hideHeader ? undefined : 'dialog-title'}
      aria-label={hideHeader ? (ariaLabel ?? t('defaultDialogAriaLabel')) : undefined}
      maxWidth={isMobile ? 'sm' : 'lg'}
      {...otherProps}
      disableRestoreFocus={disableRestoreFocus}
    >
      {/* Render Header */}
      {!hideHeader && (
        <>
          <DialogTitle id="dialog-title" sx={{ paddingTop: '24px', fontSize: '20px' }}>
            {title}
            {showCloseIcon && onClose && (
              <IconButton
                aria-label={t('closeAriaLabel')}
                onClick={(e) => onClose(e, 'escapeKeyDown')}
                sx={{
                  position: 'absolute',
                  right: isMobile ? 4 : 8,
                  top: isMobile ? 8 : 14,
                  color: 'text.primary',
                }}
              >
                <CloseOutlined className="text-[24px] text-primary-black" />
              </IconButton>
            )}
          </DialogTitle>
          {showTitleDivider && <Divider sx={{ margin: '0 24px 10px' }} />}
        </>
      )}
      <DialogContent {...contentProps}>
        {hideHeader ? (
          <div
            ref={hideHeaderContentRef}
            style={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
            }}
          >
            {children}
          </div>
        ) : (
          children
        )}
      </DialogContent>
      {footer !== undefined ? (
        footer
      ) : (
        <DialogActions sx={{ maxWidth: '484px', width: '100%', margin: '16px auto 0' }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else if (onClose) {
                onClose({}, 'backdropClick');
              }
            }}
            sx={{ flex: 1, fontFamily: 'var(--font-bricolage-bold)' }}
            loading={loading}
          >
            {cancelText}
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={onOk}
            sx={{ flex: 1, fontFamily: 'var(--font-bricolage-bold)' }}
            loading={loading}
          >
            {okText}
          </Button>
        </DialogActions>
      )}
    </MuiDialog>
  );
});

DialogBase.displayName = 'Dialog';

/**
 * Dialog.Confirm 确认弹窗
 * 只有单个确认按钮的简化版弹窗
 */
const Confirm = forwardRef<HTMLDivElement, ConfirmDialogProps>((props, ref) => {
  const { okText = 'Ok', onOk, onClose, loading, ...otherProps } = props;

  const handleOk = () => {
    if (onOk) {
      onOk();
    }
  };

  const confirmFooter = (
    <DialogActions>
      <Button
        variant="contained"
        loading={loading}
        onClick={handleOk}
        className="w-[100%] lg:max-w-[356px] h-[48px]"
        sx={{ fontFamily: 'var(--font-bricolage-bold)' }}
      >
        {okText}
      </Button>
    </DialogActions>
  );

  return <DialogBase ref={ref} onClose={onClose} footer={confirmFooter} {...otherProps} />;
});

Confirm.displayName = 'DialogConfirm';

/**
 * Dialog.Info 信息弹窗
 * 没有底部按钮，只有关闭图标的信息展示弹窗
 */
const Info = forwardRef<HTMLDivElement, InfoDialogProps>((props, ref) => {
  const { ...otherProps } = props;

  return <DialogBase ref={ref} showCloseIcon showTitleDivider footer={null} {...otherProps} />;
});

Info.displayName = 'DialogInfo';

// 创建最终的 Dialog 组件，包含子组件
const Dialog = DialogBase as DialogType;
Dialog.Confirm = Confirm;
Dialog.Info = Info;

export default Dialog;
